import { ExperienceType } from '../../../../enums/experience';
import {
    CommercialDetails,
    Experience,
    ExperienceCharge,
} from '../../../../types/experience';
import { getFeeId } from '../../Fees/ExperienceChargesForm';

export type RequiredPriceSpace = {
    feeId: string;
    spaceType: string;
};

type PricedExperience = Pick<Experience, 'type'> & {
    charges?: Experience['charges'];
    details?: Experience['details'];
};

const isExperiencePrice = (charge: ExperienceCharge) =>
    charge.chargeType === 'price' || charge.chargeType === 'flexiblePrice';

export const getMissingRequiredPrices = (
    experience: PricedExperience,
): RequiredPriceSpace[] => {
    if (experience.type !== ExperienceType.Commercial) {
        return [];
    }

    const details = (experience.details ?? {}) as Partial<CommercialDetails>;
    const commercialDetails = {
        desks: details.desks ?? 0,
        privateOffices: details.privateOffices ?? 0,
        meetingRooms: details.meetingRooms ?? 0,
        entireVenue: 1,
    };

    const availableSpaces = Object.entries(commercialDetails)
        .filter(([, value]) => Number(value) > 0)
        .map(([key]): RequiredPriceSpace => {
            switch (key) {
                case 'desks':
                    return { feeId: 'priceDesk', spaceType: 'desk' };
                case 'privateOffices':
                    return {
                        feeId: 'priceOffice',
                        spaceType: 'privateOffice',
                    };
                case 'meetingRooms':
                    return {
                        feeId: 'priceMeetingRoom',
                        spaceType: 'meetingRoom',
                    };
                default:
                    return {
                        feeId: 'priceEntireVenue',
                        spaceType: 'entireVenue',
                    };
            }
        });

    const prices = (experience.charges ?? []).filter(isExperiencePrice);

    return availableSpaces.filter(
        (space) =>
            !prices
                .map(
                    (price) =>
                        price.chargeType !== 'flexiblePrice' &&
                        'type' in price &&
                        getFeeId(price.type),
                )
                .includes(space.feeId),
    );
};

export const needsPriceConfirmation = (experience: PricedExperience) => {
    const prices = (experience.charges ?? []).filter(isExperiencePrice);
    return (
        prices.length === 0 || getMissingRequiredPrices(experience).length > 0
    );
};
